import { useEffect, useState, useCallback, useMemo } from "react";
import { devWarn } from "@/lib/log";
import { api, API, fmt, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  UsersThree,
  Crown,
  Repeat,
  Warning,
  Coffee,
  Sparkle,
  WhatsappLogo,
  Phone,
  EnvelopeSimple,
  MagnifyingGlass,
  CheckCircle,
  X,
  Robot,
  ArrowsClockwise,
  ShoppingBag,
  Calendar,
  Tag,
  Note,
  Database,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const SEGMENT_META = {
  nuevo:       { label: "Nuevo",       color: "#1E4FCC", bg: "#E3F2FD", icon: Sparkle, desc: "Primera compra" },
  recurrente:  { label: "Recurrente",  color: "#1B5E20", bg: "#E8F5E9", icon: Repeat,  desc: "Compra seguido" },
  vip:         { label: "VIP",         color: "#B45309", bg: "#FFF8E1", icon: Crown,   desc: "Alto valor" },
  en_riesgo:   { label: "En riesgo",   color: "#C62828", bg: "#FFEBEE", icon: Warning, desc: "60+ días sin volver" },
  inactivo:    { label: "Inactivo",    color: "#71717A", bg: "#F5F5F5", icon: Coffee,  desc: "120+ días sin volver" },
};

const SEGMENT_ORDER = ["vip", "recurrente", "nuevo", "en_riesgo", "inactivo"];

function StatBox({ label, value, icon: Icon, color = "#1E4FCC", onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`p-3 border border-zinc-200 bg-white text-left transition ${
        onClick ? "hover:border-klein hover:shadow-sm cursor-pointer" : "cursor-default"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted2 font-semibold">
        <Icon size={12} weight="fill" style={{ color }} />
        {label}
      </div>
      <div className="h-display text-2xl font-bold tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
    </button>
  );
}

function SegmentPill({ segment, size = "sm" }) {
  const meta = SEGMENT_META[segment] || SEGMENT_META.nuevo;
  const Icon = meta.icon;
  const sz = size === "md" ? "text-xs px-2 py-1" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 ${sz} uppercase tracking-wide font-semibold border`}
      style={{ color: meta.color, background: meta.bg, borderColor: `${meta.color}33` }}
      data-testid={`segment-pill-${segment}`}
    >
      <Icon size={size === "md" ? 12 : 10} weight="fill" />
      {meta.label}
    </span>
  );
}

function waLink(c, message) {
  const phone = c.phone_normalized || c.phone?.replace(/\D+/g, "") || "";
  if (!phone) return null;
  const text = encodeURIComponent(
    message || `Hola ${c.nombre || ""}, te escribo de parte de la tienda. ¿Cómo estás?`,
  );
  return `https://wa.me/${phone}?text=${text}`;
}

function CustomerRow({ c, active, onClick, onWhatsApp }) {
  const meta = SEGMENT_META[c.segment] || SEGMENT_META.nuevo;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`crm-row-${c.id}`}
      className={`w-full text-left p-3 border-l-4 border-b border-zinc-100 transition ${
        active ? "bg-zinc-50" : "bg-white hover:bg-zinc-50"
      }`}
      style={{ borderLeftColor: meta.color }}
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="font-semibold text-sm truncate" title={c.nombre}>
          {c.nombre || "Cliente"}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {waLink(c) && (
            <span
              role="button"
              tabIndex={0}
              title="Enviar WhatsApp ahora"
              data-testid={`crm-row-wa-${c.id}`}
              className="p-1 text-[#25D366] hover:bg-[#25D366]/10 rounded"
              onClick={(e) => {
                e.stopPropagation();
                onWhatsApp(c);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onWhatsApp(c);
                }
              }}
            >
              <WhatsappLogo size={16} weight="fill" />
            </span>
          )}
          <SegmentPill segment={c.segment} />
        </div>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-muted2 tabular-nums">
        <span title="Total acumulado" className="font-medium" style={{ color: meta.color }}>
          {fmt(c.total_gastado)}
        </span>
        <span>· {c.total_compras} compra{c.total_compras !== 1 ? "s" : ""}</span>
        {c.dias_desde_ultima !== null && (
          <span>· hace {c.dias_desde_ultima}d</span>
        )}
      </div>
      {(c.phone || c.email) && (
        <div className="flex items-center gap-2 text-[10px] text-muted2 mt-1 truncate">
          {c.phone && <><Phone size={10} /> {c.phone}</>}
          {c.email && <><EnvelopeSimple size={10} /> {c.email}</>}
        </div>
      )}
    </button>
  );
}

function CustomerDetail({ customer, onReload, settings, whatsappNumero }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", phone: "", notas: "", tags: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOutput, setAiOutput] = useState(null);

  useEffect(() => {
    if (customer) {
      setForm({
        nombre: customer.nombre || "",
        email: customer.email || "",
        phone: customer.phone || "",
        notas: customer.notas || "",
        tags: (customer.tags || []).join(", "),
      });
      setAiOutput(null);
      setEditing(false);
    }
    // Intentionally keyed on the customer IDENTITY only: re-running on every
    // object refresh would wipe the form while the user is mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  if (!customer) {
    return (
      <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center min-h-[400px] flex flex-col items-center justify-center">
        <UsersThree size={48} className="text-klein/30 mb-4" weight="duotone" />
        <p className="text-sm text-muted2">
          Selecciona un cliente para ver su historial y enviarle un mensaje personalizado.
        </p>
      </div>
    );
  }

  const meta = SEGMENT_META[customer.segment] || SEGMENT_META.nuevo;

  const save = async () => {
    try {
      const payload = {
        nombre: form.nombre.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notas: form.notas.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.put(`/customers/${customer.id}`, payload);
      toast.success("Perfil actualizado");
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo guardar"));
    }
  };

  const askAi = async (intent) => {
    setAiBusy(true);
    setAiOutput(null);
    try {
      const { data } = await api.post(`/customers/${customer.id}/ai-message`, {
        intent,
        discount_pct: intent === "reactivacion" ? 10 : 0,
      });
      setAiOutput(data);
    } catch (e) {
      toast.error(formatApiError(e, "La IA no pudo generar el mensaje"));
    } finally {
      setAiBusy(false);
    }
  };

  const registerEngagement = async (channel) => {
    try {
      await api.post(`/customers/${customer.id}/engagement`, {
        channel,
        message: aiOutput?.message || "",
      });
      toast.success("Registrado en bitácora");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo registrar"));
    }
  };

  const openWhatsApp = () => {
    const phone = customer.phone_normalized || customer.phone?.replace(/\D+/g, "") || "";
    if (!phone) {
      toast.error("Este cliente no tiene teléfono");
      return;
    }
    const text = encodeURIComponent(aiOutput?.message || `Hola ${customer.nombre || ""}, te escribo de parte de la tienda. ¿Cómo estás?`);
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener");
    // Also auto-register engagement after a short delay so the bitácora reflects intent
    setTimeout(() => registerEngagement("whatsapp"), 500);
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="crm-detail">
      <header className="p-4 border-b border-zinc-200" style={{ background: meta.bg }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <SegmentPill segment={customer.segment} size="md" />
              {customer.dias_desde_ultima !== null && (
                <span className="text-[10px] uppercase tracking-wide text-muted2">
                  {customer.dias_desde_ultima === 0 ? "hoy" : `hace ${customer.dias_desde_ultima} días`}
                </span>
              )}
            </div>
            <h2 className="h-display text-2xl font-bold tracking-tight">{customer.nombre || "Cliente"}</h2>
            <p className="text-xs text-muted2 mt-0.5">{meta.desc}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className="text-xs uppercase tracking-wide font-semibold px-2.5 py-1 border border-zinc-400 bg-white hover:bg-zinc-50"
            data-testid="crm-edit-toggle"
          >
            {editing ? "Cancelar" : "Editar"}
          </button>
        </div>
      </header>

      <div className="p-4 grid grid-cols-3 gap-3 border-b border-zinc-100">
        <KpiCell label="Gasto total" value={fmt(customer.total_gastado)} accent={meta.color} />
        <KpiCell label="Compras" value={customer.total_compras} />
        <KpiCell label="Ticket promedio" value={fmt(customer.ticket_promedio)} />
      </div>

      {editing ? (
        <div className="p-4 space-y-3 border-b border-zinc-100">
          <Field label="Nombre">
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-zinc-300" data-testid="crm-input-nombre" />
          </Field>
          <Field label="Teléfono">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-zinc-300" data-testid="crm-input-phone" />
          </Field>
          <Field label="Email">
            <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-zinc-300" data-testid="crm-input-email" />
          </Field>
          <Field label="Etiquetas (separadas por coma)">
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-zinc-300" data-testid="crm-input-tags" placeholder="cumple-enero, premium" />
          </Field>
          <Field label="Notas internas">
            <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-zinc-300" data-testid="crm-input-notas" />
          </Field>
          <div className="flex gap-2">
            <button type="button" onClick={save} className="px-3 py-1.5 text-xs uppercase tracking-wide font-semibold bg-klein text-white hover:bg-klein/90" data-testid="crm-save-btn">
              Guardar
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-2 border-b border-zinc-100 text-sm">
          {customer.phone && (
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-muted2" /> <span className="tabular-nums">{customer.phone}</span>
              <button
                type="button"
                onClick={openWhatsApp}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-[#25D366] text-white font-semibold hover:bg-[#1da851]"
                title="Abre WhatsApp con el último mensaje IA (o un saludo) y lo registra en la bitácora"
                data-testid="crm-detail-wa"
              >
                <WhatsappLogo size={11} weight="fill" /> WhatsApp
              </button>
            </div>
          )}
          {customer.email && (
            <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-muted2" /> {customer.email}</div>
          )}
          {customer.tags?.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap mt-1">
              <Tag size={12} className="text-muted2" />
              {customer.tags.map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700">{t}</span>
              ))}
            </div>
          )}
          {customer.notas && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 text-xs">
              <Note size={14} className="text-amber-700 mt-0.5" />
              <p className="whitespace-pre-wrap">{customer.notas}</p>
            </div>
          )}
          {customer.last_engagement_at && (
            <div className="text-[11px] text-muted2 mt-2 italic">
              Última vez contactado: {new Date(customer.last_engagement_at).toLocaleString()} ({customer.last_engagement_channel})
            </div>
          )}
        </div>
      )}

      {/* AI re-engagement */}
      <div className="p-4 border-b border-zinc-100">
        <div className="text-[10px] uppercase tracking-wide font-semibold text-muted2 mb-2 flex items-center gap-1.5">
          <Robot size={12} /> Mensaje personalizado con IA
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <button type="button" disabled={aiBusy} onClick={() => askAi("reactivacion")} className="px-2.5 py-1 text-xs border border-klein text-klein hover:bg-klein hover:text-white disabled:opacity-50" data-testid="crm-ai-reactivar">
            {aiBusy ? "Generando…" : "Reactivar"}
          </button>
          <button type="button" disabled={aiBusy} onClick={() => askAi("vip_thanks")} className="px-2.5 py-1 text-xs border border-zinc-400 text-ink hover:bg-zinc-50 disabled:opacity-50" data-testid="crm-ai-vip">
            Agradecer VIP
          </button>
          <button type="button" disabled={aiBusy} onClick={() => askAi("new_promo")} className="px-2.5 py-1 text-xs border border-zinc-400 text-ink hover:bg-zinc-50 disabled:opacity-50" data-testid="crm-ai-promo">
            Anunciar promo
          </button>
        </div>
        {aiOutput && (
          <div className="p-3 border border-klein/30 bg-blue-50/50 space-y-2" data-testid="crm-ai-output">
            <p className="text-sm whitespace-pre-wrap font-medium">{aiOutput.message}</p>
            {aiOutput.reasoning && (
              <p className="text-[11px] text-muted2 italic">💡 {aiOutput.reasoning}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-klein/20">
              <button type="button" onClick={openWhatsApp} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-[#25D366] text-white font-semibold hover:bg-[#1da851]" data-testid="crm-ai-send-wa">
                <WhatsappLogo size={12} weight="fill" /> Abrir WhatsApp
              </button>
              <button type="button" onClick={() => { navigator.clipboard.writeText(aiOutput.message); toast.success("Copiado"); }} className="px-2.5 py-1 text-xs border border-zinc-400 text-ink hover:bg-zinc-50" data-testid="crm-ai-copy">
                Copiar mensaje
              </button>
              <button type="button" onClick={() => registerEngagement("manual")} className="px-2.5 py-1 text-xs border border-zinc-400 text-muted2 hover:bg-zinc-50" data-testid="crm-ai-mark-contacted">
                Marcar contactado
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Productos preferidos */}
      {customer.productos_preferidos?.length > 0 && (
        <div className="p-4 border-b border-zinc-100">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-muted2 mb-2 flex items-center gap-1.5">
            <ShoppingBag size={12} /> Productos favoritos
          </div>
          <div className="space-y-1">
            {customer.productos_preferidos.slice(0, 5).map((p) => (
              <div key={p.product_id} className="flex items-center justify-between text-sm py-1 border-b border-zinc-50 last:border-0">
                <span className="truncate" title={p.product_nombre}>{p.product_nombre || "(sin nombre)"}</span>
                <span className="text-[10px] uppercase tracking-wide bg-zinc-100 px-1.5 py-0.5 font-mono">x{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historial */}
      {customer.sales?.length > 0 && (
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-muted2 mb-2 flex items-center gap-1.5">
            <Calendar size={12} /> Historial de compras ({customer.sales.length})
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {customer.sales.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 text-xs p-2 border border-zinc-100 hover:bg-zinc-50">
                <div className="min-w-0">
                  <div className="font-medium truncate" title={s.product_nombre}>{s.product_nombre}</div>
                  <div className="text-[10px] text-muted2">
                    {new Date(s.created_at).toLocaleDateString()} · {s.canal || "—"} · {s.estado}
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div className="font-semibold">{fmt(s.total)}</div>
                  <div className="text-[10px] text-muted2">x{s.cantidad}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide text-muted2 font-semibold block mb-1">{label}</span>
      {children}
    </label>
  );
}

function KpiCell({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted2 font-semibold">{label}</div>
      <div className="h-display text-xl font-bold tabular-nums" style={{ color: accent || "#0A0A0A" }}>{value}</div>
    </div>
  );
}

export default function Crm() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [segment, setSegment] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (segment) params.append("segment", segment);
      if (search.trim()) params.append("search", search.trim());
      params.append("limit", "300");
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/customers?${params.toString()}`),
        api.get(`/customers/stats`),
      ]);
      setItems(list.items || []);
      setStats(s);
    } catch (e) {
      devWarn("crm load failed", e);
      toast.error(formatApiError(e, "No se pudo cargar el CRM"));
    } finally {
      setLoading(false);
    }
  }, [segment, search]);

  // Quick re-engagement: open WhatsApp with a greeting straight from the
  // customer list (1 click) and log the touch in the engagement bitácora.
  const quickWhatsApp = useCallback(async (c) => {
    const url = waLink(c);
    if (!url) {
      toast.error("Este cliente no tiene teléfono");
      return;
    }
    window.open(url, "_blank", "noopener");
    try {
      await api.post(`/customers/${c.id}/engagement`, { channel: "whatsapp", message: "" });
      toast.success("Contacto registrado en bitácora");
      load();
    } catch {
      /* engagement logging is best-effort — the WhatsApp tab already opened */
    }
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/customers/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo cargar el cliente"));
    }
  }, []);

  useEffect(() => {
    if (selected) loadDetail(selected);
    else setDetail(null);
  }, [selected, loadDetail]);

  const filtered = useMemo(() => items, [items]);

  const runBackfill = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Reconstruir el CRM lee todas las ventas y reagrupa por cliente. ¿Continuar?")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/customers/backfill");
      toast.success(`Listo: ${data.customers_total} clientes a partir de ${data.sales_processed} ventas`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Fallo el rebuild"));
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 md:px-10 py-8 max-w-7xl mx-auto" data-testid="crm-page">
      <header className="mb-6">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            CRM
          </h1>
          <span className="text-sm text-muted2 font-medium">
            Compradores fieles · Re-engagement con IA · Alertas automáticas
          </span>
        </div>
        <p className="text-xs text-muted2 mt-2">
          Cada venta crea/actualiza un perfil de cliente. La IA te sugiere mensajes personalizados para reactivar a los que se están enfriando.
        </p>
      </header>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <StatBox testid="kpi-total" label="Total clientes" value={stats.total} icon={UsersThree} onClick={() => setSegment("")} />
          <StatBox testid="kpi-vip" label="VIP" value={stats.by_segment?.vip || 0} icon={Crown} color="#B45309" onClick={() => setSegment("vip")} />
          <StatBox testid="kpi-recurrente" label="Recurrentes" value={stats.by_segment?.recurrente || 0} icon={Repeat} color="#1B5E20" onClick={() => setSegment("recurrente")} />
          <StatBox testid="kpi-nuevo" label="Nuevos" value={stats.by_segment?.nuevo || 0} icon={Sparkle} color="#1E4FCC" onClick={() => setSegment("nuevo")} />
          <StatBox testid="kpi-riesgo" label="En riesgo" value={stats.by_segment?.en_riesgo || 0} icon={Warning} color="#C62828" onClick={() => setSegment("en_riesgo")} />
          <StatBox testid="kpi-inactivo" label="Inactivos" value={stats.by_segment?.inactivo || 0} icon={Coffee} color="#71717A" onClick={() => setSegment("inactivo")} />
        </div>
      )}

      {stats && stats.pending_engagement > 0 && (
        <div className="mb-4 p-3 border-l-4 border-[#C62828] bg-[#FFEBEE] flex items-start gap-3" data-testid="crm-engagement-banner">
          <Warning size={20} className="text-[#C62828] shrink-0 mt-0.5" weight="fill" />
          <div className="text-sm flex-1">
            <div className="font-semibold text-[#B71C1C]">
              {stats.pending_engagement} cliente{stats.pending_engagement !== 1 ? "s" : ""} en riesgo sin contactar
            </div>
            <p className="text-xs text-muted2 mt-0.5">
              Llevan más de {stats.thresholds?.en_riesgo_days || 60} días sin volver. Filtra &quot;En riesgo&quot; y mándales un mensaje personalizado con IA.
            </p>
          </div>
          <button type="button" onClick={() => setSegment("en_riesgo")} className="text-xs uppercase tracking-wide font-semibold px-3 py-1.5 bg-[#C62828] text-white hover:bg-[#B71C1C]" data-testid="crm-banner-action">
            Ver lista →
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List column */}
        <section className="lg:col-span-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
              <input
                type="text"
                placeholder="Buscar nombre, teléfono, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
                data-testid="crm-search"
              />
            </div>
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              className="px-2 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
              data-testid="crm-filter-segment"
            >
              <option value="">Todos los segmentos</option>
              {SEGMENT_ORDER.map((seg) => (
                <option key={seg} value={seg}>{SEGMENT_META[seg].label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={load}
              className="p-2 border border-zinc-300 text-muted2 hover:bg-zinc-50"
              title="Refrescar"
              data-testid="crm-refresh"
            >
              <ArrowsClockwise size={14} />
            </button>
            {isAdmin && (
              <button
                type="button"
                onClick={runBackfill}
                disabled={backfilling}
                className="inline-flex items-center gap-1 px-2 py-2 text-xs uppercase tracking-wide font-semibold border border-zinc-400 text-muted2 hover:bg-zinc-50 disabled:opacity-50"
                title="Reconstruir el CRM desde las ventas existentes"
                data-testid="crm-backfill"
              >
                <Database size={12} /> {backfilling ? "…" : "Rebuild"}
              </button>
            )}
          </div>

          <div className="border border-zinc-200 bg-white max-h-[calc(100vh-320px)] overflow-y-auto" data-testid="crm-list">
            {loading ? (
              <div className="text-center text-sm text-muted2 py-12">Cargando…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 px-4">
                <UsersThree size={36} className="mx-auto text-klein/40 mb-3" weight="duotone" />
                <p className="text-sm font-medium">
                  {search || segment ? "Sin resultados" : "Aún no hay clientes"}
                </p>
                <p className="text-xs text-muted2 mt-1">
                  {search || segment
                    ? "Cambia los filtros o limpia la búsqueda."
                    : "Al registrar una venta con teléfono o email, el cliente aparecerá aquí automáticamente."}
                </p>
                {isAdmin && !search && !segment && (
                  <button type="button" onClick={runBackfill} disabled={backfilling} className="mt-3 px-3 py-1.5 text-xs uppercase tracking-wide font-semibold border border-klein text-klein hover:bg-klein hover:text-white disabled:opacity-50">
                    {backfilling ? "…" : "Rebuild desde ventas"}
                  </button>
                )}
              </div>
            ) : (
              filtered.map((c) => (
                <CustomerRow
                  key={c.id}
                  c={c}
                  active={selected === c.id}
                  onClick={() => setSelected(c.id)}
                  onWhatsApp={quickWhatsApp}
                />
              ))
            )}
          </div>
          {filtered.length > 0 && (
            <p className="mt-2 text-[10px] text-muted2 tabular-nums">
              {filtered.length} cliente{filtered.length !== 1 ? "s" : ""} {segment ? `· ${SEGMENT_META[segment].label}` : ""}
            </p>
          )}
        </section>

        {/* Detail column */}
        <section className="lg:col-span-7">
          <CustomerDetail customer={detail} onReload={() => { load(); if (selected) loadDetail(selected); }} />
        </section>
      </div>
    </div>
  );
}
